import React, { useState, useEffect, useRef } from 'react';
import { Search, Building, X } from 'lucide-react';
import { API_URL } from '../config';
import { SkeletonBlock } from './Skeleton';
import FloatingField from './FloatingField';

const CompanySelector = ({ onSelect, label = 'Компания' }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchTimeout = useRef(null);

  // Закрытие при клике вне
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Поиск с debounce
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }

    searchTimeout.current = setTimeout(async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/dadata/organizations?query=${encodeURIComponent(query)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setSuggestions(data);
          setShowSuggestions(true);
        }
      } catch (error) {
        console.error('Ошибка поиска:', error);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(searchTimeout.current);
  }, [query]);

  const selectCompany = (company) => {
    setSelectedCompany(company);
    setShowSuggestions(false);
    
    // Определяем тип организации
    const isIP = company.data.type === 'INDIVIDUAL';
    
    // Формируем данные для отправки
    const companyData = {
      company_name: company.data.short_name || company.value,
      full_name: company.data.full_name,
      short_name: company.data.short_name,
      inn: company.data.inn,
      kpp: isIP ? null : company.data.kpp,  // У ИП нет КПП
      legal_type: isIP ? 'ИП' : 'ООО',
      ogrn: company.data.ogrn,
      position: isIP ? 'Индивидуальный предприниматель' : (company.data.management || 'Генеральный директор'),
      legal_address: company.data.legal_address,
      contact_person: company.data.management
    };
    
    onSelect(companyData);
  };

  const clearSelection = () => {
    setSelectedCompany(null);
    setQuery('');
    setSuggestions([]);
    onSelect(null);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <FloatingField label={label} labelOffset="2.5rem" filled={!!(selectedCompany || query)} active={showSuggestions}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
          ref={inputRef}
          type="text"
          value={selectedCompany ? selectedCompany.value : query}
          onChange={(e) => {
            setQuery(e.target.value);
            if (selectedCompany) clearSelection();
          }}
          onFocus={() => query.length >= 2 && setShowSuggestions(true)}
          className="w-full rounded-xl border border-gray-300 py-2.5 pl-10 pr-10 text-[15px] placeholder:text-[14px] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
          placeholder="ИНН, ОГРН или название"
        />
          {selectedCompany && (
            <button
              type="button"
              onClick={clearSelection}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          )}
        </div>
      </FloatingField>

      {/* Подсказки */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-xl border border-gray-300 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
          {suggestions.map((company, idx) => (
            <button
              key={idx}
              onClick={() => selectCompany(company)}
              className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
            >
              <div className="flex items-center space-x-2">
                <Building className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="truncate text-[13px] font-medium text-gray-900 dark:text-gray-100">
                    {company.value}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    ИНН: {company.data.inn}
                    {company.data.kpp && ` • КПП: ${company.data.kpp}`}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {company.data.type === 'INDIVIDUAL' ? 'ИП' : 'ООО'}
                    {company.data.legal_address && ` • ${company.data.legal_address}`}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {loading && (
        <div className="absolute z-20 mt-1 w-full space-y-2 rounded-xl border border-gray-300 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-800" role="status" aria-label="Загрузка компаний">
          <SkeletonBlock className="h-10 w-full" />
          <SkeletonBlock className="h-10 w-full" />
          <SkeletonBlock className="h-10 w-full" />
        </div>
      )}
    </div>
  );
};

export default CompanySelector;
